import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AdminUser,
  AdminEntity,
  AdminTable,
  ArchivedTable,
  DeletedTable,
  AuditEntry,
  AuditLogResponse,
  Role,
  UserStatus,
  SupportTicket,
} from '@command-floor/shared-types';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { SupportTicketBoard } from '../features/admin/SupportTicketBoard.js';

type Tab = 'users' | 'hierarchy' | 'tables' | 'archived' | 'deleted' | 'audit' | 'support';

export function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('users');

  if (!user) return null;
  if (user.role !== 'LEADERSHIP' && user.role !== 'ENTITY_MANAGER') {
    navigate('/');
    return null;
  }

  const isLeadership = user.role === 'LEADERSHIP';

  const tabs: { id: Tab; label: string; show: boolean }[] = [
    { id: 'users', label: 'Users', show: isLeadership },
    { id: 'hierarchy', label: 'Hierarchy', show: isLeadership },
    { id: 'tables', label: 'Tables', show: true },
    { id: 'archived', label: 'Archived', show: true },
    { id: 'deleted', label: 'Recently Deleted', show: isLeadership },
    { id: 'audit', label: 'Audit Log', show: true },
    { id: 'support', label: 'Support Tickets', show: isLeadership },
  ];

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-5xl px-6 py-8">
        <h1 className="mb-6 text-xl font-semibold text-slate-100">Admin</h1>

        <div className="mb-6 flex gap-1 border-b border-aurora">
          {tabs
            .filter((t) => t.show)
            .map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  tab === t.id
                    ? 'border-b-2 border-flag-blue text-slate-100'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {t.label}
              </button>
            ))}
        </div>

        {tab === 'users' && <UsersTab />}
        {tab === 'hierarchy' && <HierarchyTab />}
        {tab === 'tables' && <TablesTab />}
        {tab === 'archived' && <ArchivedTab />}
        {tab === 'deleted' && <DeletedTab />}
        {tab === 'audit' && <AuditTab />}
        {tab === 'support' && <SupportTab />}
      </div>
    </div>
  );
}

// ─── Users Tab ────────────────────────────────────────────────────────────────

function UsersTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const {
    data: users = [],
    isLoading,
    error: usersError,
  } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<AdminUser[]>('/api/admin/users'),
  });

  const { data: hierarchy, error: hierarchyError } = useQuery({
    queryKey: ['admin-hierarchy-full'],
    queryFn: () => api.get<{ entities: AdminEntity[] }>('/api/admin/hierarchy/full'),
  });
  const entities = hierarchy?.entities ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-users'] });

  if (isLoading) return <p className="text-slate-500 text-sm">Loading…</p>;
  if (usersError)
    return (
      <p className="text-sm text-red-400">Error loading users: {(usersError as Error).message}</p>
    );

  return (
    <div>
      {!showCreate && (
        <button
          onClick={() => setShowCreate(true)}
          className="mb-4 rounded bg-slate-700 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-600"
        >
          + New user
        </button>
      )}

      {showCreate && (
        <CreateUserForm
          entities={entities}
          onDone={() => {
            setShowCreate(false);
            invalidate();
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-aurora text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="pb-2 pr-4">Name</th>
            <th className="pb-2 pr-4">Email</th>
            <th className="pb-2 pr-4">Role</th>
            <th className="pb-2 pr-4">Status</th>
            <th className="pb-2">Last login</th>
            <th className="pb-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-aurora">
          {users.map((u) => (
            <tr key={u.id}>
              <td className="py-2 pr-4 text-slate-200">
                {u.display_name}
                {u.title && <span className="ml-1 text-xs text-slate-500">({u.title})</span>}
              </td>
              <td className="py-2 pr-4 text-slate-400">{u.email}</td>
              <td className="py-2 pr-4 text-slate-400">{u.role}</td>
              <td className="py-2 pr-4">
                <span
                  className={`text-xs font-medium ${u.status === 'ACTIVE' ? 'text-green-400' : 'text-slate-500'}`}
                >
                  {u.status}
                </span>
              </td>
              <td className="py-2 text-xs text-slate-500">
                {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : '—'}
              </td>
              <td className="py-2 pl-4">
                <button
                  onClick={() => setEditingId(editingId === u.id ? null : u.id)}
                  className="text-xs text-slate-400 hover:text-slate-200 underline"
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editingId && (
        <EditUserModal
          user={users.find((u) => u.id === editingId)!}
          entities={entities}
          onDone={() => {
            setEditingId(null);
            invalidate();
          }}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  );
}

function CreateUserForm({
  entities,
  onDone,
  onCancel,
}: {
  entities: AdminEntity[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [title, setTitle] = useState('');
  const [role, setRole] = useState<Role>('TEAM_MEMBER');
  const [scopeIds, setScopeIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/admin/users', {
        email,
        display_name: displayName,
        title: title || undefined,
        role,
        entity_scope_ids: role === 'ENTITY_MANAGER' ? scopeIds : undefined,
      }),
    onSuccess: onDone,
    onError: (err: Error) => setError(err.message),
  });

  const activeEntities = entities.filter((e) => e.status === 'ACTIVE');

  return (
    <div className="mb-6 rounded border border-aurora-strong bg-slate-800 p-4 space-y-3">
      <h3 className="text-sm font-medium text-slate-300">New user</h3>
      <div className="grid grid-cols-2 gap-3">
        <input
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          type="email"
          className="rounded border border-aurora-strong bg-slate-900 px-2 py-1.5 text-sm text-slate-200 placeholder-slate-600"
        />
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Display name"
          className="rounded border border-aurora-strong bg-slate-900 px-2 py-1.5 text-sm text-slate-200 placeholder-slate-600"
        />
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (optional)"
          className="rounded border border-aurora-strong bg-slate-900 px-2 py-1.5 text-sm text-slate-200 placeholder-slate-600"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          className="rounded border border-aurora-strong bg-slate-900 px-2 py-1.5 text-sm text-slate-200"
        >
          <option value="TEAM_MEMBER">Team Member</option>
          <option value="ENTITY_MANAGER">Entity Manager</option>
          <option value="LEADERSHIP">Leadership</option>
        </select>
      </div>
      {role === 'ENTITY_MANAGER' && activeEntities.length > 0 && (
        <div>
          <p className="mb-1 text-xs text-slate-400">Entity scopes</p>
          <div className="flex flex-wrap gap-2">
            {activeEntities.map((e) => (
              <label key={e.id} className="flex items-center gap-1.5 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={scopeIds.includes(e.id)}
                  onChange={(ev) =>
                    setScopeIds(
                      ev.target.checked
                        ? [...scopeIds, e.id]
                        : scopeIds.filter((id) => id !== e.id),
                    )
                  }
                  className="accent-flag-blue"
                />
                {e.name}
              </label>
            ))}
          </div>
        </div>
      )}
      {error && <p className="text-xs text-red-400">{error}</p>}
      <div className="flex gap-2">
        <button
          onClick={() => create.mutate()}
          disabled={!email.trim() || !displayName.trim() || create.isPending}
          className="rounded bg-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
        >
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
        <button onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-300">
          Cancel
        </button>
      </div>
    </div>
  );
}

function EditUserModal({
  user,
  entities,
  onDone,
  onClose,
}: {
  user: AdminUser;
  entities: AdminEntity[];
  onDone: () => void;
  onClose: () => void;
}) {
  const [displayName, setDisplayName] = useState(user.display_name);
  const [title, setTitle] = useState(user.title ?? '');
  const [role, setRole] = useState<Role>(user.role);
  const [status, setStatus] = useState<UserStatus>(user.status);
  const [scopeIds, setScopeIds] = useState<string[]>(user.entity_scope_ids);
  const [error, setError] = useState<string | null>(null);

  const patch = useMutation({
    mutationFn: () =>
      api.patch(`/api/admin/users/${user.id}`, {
        display_name: displayName,
        title: title || null,
        role,
        status,
      }),
    onSuccess: async () => {
      await updateScopes.mutateAsync();
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateScopes = useMutation({
    mutationFn: () => api.put(`/api/admin/users/${user.id}/scopes`, { entity_scope_ids: scopeIds }),
    onSuccess: onDone,
    onError: (err: Error) => setError(err.message),
  });

  const activeEntities = entities.filter((e) => e.status === 'ACTIVE');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-md rounded-lg border border-aurora-strong bg-slate-900 p-6 space-y-4 shadow-xl">
        <h3 className="text-base font-medium text-slate-100">Edit {user.display_name}</h3>
        <div className="space-y-3">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Display name"
            className="w-full rounded border border-aurora-strong bg-slate-800 px-2 py-1.5 text-sm text-slate-200"
          />
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className="w-full rounded border border-aurora-strong bg-slate-800 px-2 py-1.5 text-sm text-slate-200"
          />
          <div className="flex gap-3">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="flex-1 rounded border border-aurora-strong bg-slate-800 px-2 py-1.5 text-sm text-slate-200"
            >
              <option value="TEAM_MEMBER">Team Member</option>
              <option value="ENTITY_MANAGER">Entity Manager</option>
              <option value="LEADERSHIP">Leadership</option>
            </select>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as UserStatus)}
              className="flex-1 rounded border border-aurora-strong bg-slate-800 px-2 py-1.5 text-sm text-slate-200"
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
          {role === 'ENTITY_MANAGER' && activeEntities.length > 0 && (
            <div>
              <p className="mb-1 text-xs text-slate-400">Entity scopes</p>
              <div className="flex flex-wrap gap-2">
                {activeEntities.map((e) => (
                  <label key={e.id} className="flex items-center gap-1.5 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={scopeIds.includes(e.id)}
                      onChange={(ev) =>
                        setScopeIds(
                          ev.target.checked
                            ? [...scopeIds, e.id]
                            : scopeIds.filter((id) => id !== e.id),
                        )
                      }
                      className="accent-flag-blue"
                    />
                    {e.name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        {error && <p className="text-xs text-red-400">{error}</p>}
        <div className="flex gap-2">
          <button
            onClick={() => patch.mutate()}
            disabled={!displayName.trim() || patch.isPending || updateScopes.isPending}
            className="rounded bg-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
          >
            {patch.isPending || updateScopes.isPending ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-300">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Hierarchy Tab ────────────────────────────────────────────────────────────

function HierarchyTab() {
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-hierarchy-full'],
    queryFn: () => api.get<{ entities: AdminEntity[] }>('/api/admin/hierarchy/full'),
  });
  const entities = data?.entities ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-hierarchy-full'] });

  if (isLoading) return <p className="text-slate-500 text-sm">Loading…</p>;
  if (error)
    return (
      <p className="text-sm text-red-400">Error loading hierarchy: {(error as Error).message}</p>
    );

  return (
    <div className="space-y-4">
      <AddEntityForm onDone={invalidate} />
      {entities.map((entity) => (
        <EntityBlock key={entity.id} entity={entity} onChanged={invalidate} />
      ))}
      {entities.length === 0 && <p className="text-sm italic text-slate-600">No entities yet.</p>}
    </div>
  );
}

function AddEntityForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [show, setShow] = useState(false);

  const create = useMutation({
    mutationFn: () => api.post('/api/admin/entities', { name }),
    onSuccess: () => {
      setName('');
      setShow(false);
      onDone();
    },
  });

  if (!show) {
    return (
      <button
        onClick={() => setShow(true)}
        className="rounded bg-slate-700 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-600"
      >
        + New entity
      </button>
    );
  }

  return (
    <div className="flex gap-2 items-center">
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Entity name"
        className="rounded border border-aurora-strong bg-slate-800 px-2 py-1 text-sm text-slate-200 placeholder-slate-600"
      />
      <button
        onClick={() => create.mutate()}
        disabled={!name.trim() || create.isPending}
        className="rounded bg-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
      >
        {create.isPending ? 'Creating…' : 'Create'}
      </button>
      <button
        onClick={() => setShow(false)}
        className="text-xs text-slate-500 hover:text-slate-300"
      >
        Cancel
      </button>
    </div>
  );
}

function EntityBlock({ entity, onChanged }: { entity: AdminEntity; onChanged: () => void }) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(entity.name);
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');

  const patchEntity = useMutation({
    mutationFn: (data: { name?: string; status?: 'ACTIVE' | 'INACTIVE' }) =>
      api.patch(`/api/admin/entities/${entity.id}`, data),
    onSuccess: () => {
      setEditingName(false);
      onChanged();
    },
  });

  const addCategory = useMutation({
    mutationFn: () => api.post(`/api/admin/entities/${entity.id}/categories`, { name: newCatName }),
    onSuccess: () => {
      setNewCatName('');
      setShowAddCat(false);
      onChanged();
    },
  });

  return (
    <div className="rounded border border-aurora bg-surface-panel p-4">
      <div className="mb-3 flex items-center justify-between">
        {editingName ? (
          <div className="flex gap-2 items-center">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded border border-aurora-strong bg-slate-800 px-2 py-0.5 text-sm text-slate-200"
            />
            <button
              onClick={() => patchEntity.mutate({ name })}
              disabled={!name.trim() || patchEntity.isPending}
              className="rounded bg-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
            >
              Save
            </button>
            <button
              onClick={() => {
                setName(entity.name);
                setEditingName(false);
              }}
              className="text-xs text-slate-500 hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <span className="font-medium text-slate-100">{entity.name}</span>
            <span
              className={`text-xs ${entity.status === 'ACTIVE' ? 'text-green-400' : 'text-slate-500'}`}
            >
              {entity.status}
            </span>
          </div>
        )}
        <div className="flex gap-2">
          {!editingName && (
            <button
              onClick={() => setEditingName(true)}
              className="text-xs text-slate-500 hover:text-slate-300 underline"
            >
              Rename
            </button>
          )}
          <button
            onClick={() =>
              patchEntity.mutate({
                status: entity.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
              })
            }
            className="text-xs text-slate-500 hover:text-slate-300 underline"
          >
            {entity.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>

      <div className="ml-4 space-y-2">
        {entity.categories.map((cat) => (
          <CategoryBlock key={cat.id} category={cat} onChanged={onChanged} />
        ))}

        {showAddCat ? (
          <div className="flex gap-2 items-center mt-2">
            <input
              autoFocus
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              placeholder="Category name"
              className="rounded border border-aurora-strong bg-slate-800 px-2 py-0.5 text-sm text-slate-200 placeholder-slate-600"
            />
            <button
              onClick={() => addCategory.mutate()}
              disabled={!newCatName.trim() || addCategory.isPending}
              className="rounded bg-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
            >
              {addCategory.isPending ? 'Adding…' : 'Add'}
            </button>
            <button
              onClick={() => setShowAddCat(false)}
              className="text-xs text-slate-500 hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowAddCat(true)}
            className="mt-1 text-xs text-slate-500 hover:text-slate-300 underline"
          >
            + Add category
          </button>
        )}
      </div>
    </div>
  );
}

function CategoryBlock({
  category,
  onChanged,
}: {
  category: AdminEntity['categories'][number];
  onChanged: () => void;
}) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(category.name);
  const [showAddSub, setShowAddSub] = useState(false);
  const [newSubName, setNewSubName] = useState('');

  const patchCategory = useMutation({
    mutationFn: (data: { name?: string; status?: 'ACTIVE' | 'INACTIVE' }) =>
      api.patch(`/api/admin/categories/${category.id}`, data),
    onSuccess: () => {
      setEditingName(false);
      onChanged();
    },
  });

  const addSub = useMutation({
    mutationFn: () =>
      api.post(`/api/admin/categories/${category.id}/subcategories`, { name: newSubName }),
    onSuccess: () => {
      setNewSubName('');
      setShowAddSub(false);
      onChanged();
    },
  });

  return (
    <div className="rounded border border-aurora/50 bg-slate-800/40 px-3 py-2">
      <div className="flex items-center justify-between">
        {editingName ? (
          <div className="flex gap-2 items-center">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded border border-aurora-strong bg-slate-900 px-2 py-0.5 text-sm text-slate-200"
            />
            <button
              onClick={() => patchCategory.mutate({ name })}
              disabled={!name.trim() || patchCategory.isPending}
              className="rounded bg-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
            >
              Save
            </button>
            <button
              onClick={() => {
                setName(category.name);
                setEditingName(false);
              }}
              className="text-xs text-slate-500 hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-200">{category.name}</span>
            <span
              className={`text-xs ${category.status === 'ACTIVE' ? 'text-green-400' : 'text-slate-500'}`}
            >
              {category.status === 'INACTIVE' ? 'inactive' : ''}
            </span>
          </div>
        )}
        <div className="flex gap-2">
          {!editingName && (
            <button
              onClick={() => setEditingName(true)}
              className="text-xs text-slate-600 hover:text-slate-400 underline"
            >
              Rename
            </button>
          )}
          <button
            onClick={() =>
              patchCategory.mutate({
                status: category.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
              })
            }
            className="text-xs text-slate-600 hover:text-slate-400 underline"
          >
            {category.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>

      {category.subcategories.length > 0 && (
        <ul className="ml-3 mt-1 space-y-0.5">
          {category.subcategories.map((sub) => (
            <SubcategoryRow key={sub.id} sub={sub} onChanged={onChanged} />
          ))}
        </ul>
      )}

      {showAddSub ? (
        <div className="ml-3 mt-1 flex gap-2 items-center">
          <input
            autoFocus
            value={newSubName}
            onChange={(e) => setNewSubName(e.target.value)}
            placeholder="Subcategory name"
            className="rounded border border-aurora-strong bg-slate-900 px-2 py-0.5 text-xs text-slate-200 placeholder-slate-600"
          />
          <button
            onClick={() => addSub.mutate()}
            disabled={!newSubName.trim() || addSub.isPending}
            className="rounded bg-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
          >
            {addSub.isPending ? 'Adding…' : 'Add'}
          </button>
          <button
            onClick={() => setShowAddSub(false)}
            className="text-xs text-slate-500 hover:text-slate-300"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setShowAddSub(true)}
          className="ml-3 mt-1 text-xs text-slate-600 hover:text-slate-400 underline"
        >
          + Add subcategory
        </button>
      )}
    </div>
  );
}

function SubcategoryRow({
  sub,
  onChanged,
}: {
  sub: { id: string; name: string; status: 'ACTIVE' | 'INACTIVE' };
  onChanged: () => void;
}) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(sub.name);

  const patch = useMutation({
    mutationFn: (data: { name?: string; status?: 'ACTIVE' | 'INACTIVE' }) =>
      api.patch(`/api/admin/subcategories/${sub.id}`, data),
    onSuccess: () => {
      setEditingName(false);
      onChanged();
    },
  });

  return (
    <li className="flex items-center justify-between">
      {editingName ? (
        <div className="flex gap-2 items-center">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded border border-aurora-strong bg-slate-900 px-1.5 py-0.5 text-xs text-slate-200"
          />
          <button
            onClick={() => patch.mutate({ name })}
            disabled={!name.trim() || patch.isPending}
            className="rounded bg-slate-600 px-1.5 py-0.5 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
          >
            Save
          </button>
          <button
            onClick={() => {
              setName(sub.name);
              setEditingName(false);
            }}
            className="text-xs text-slate-600 hover:text-slate-400"
          >
            Cancel
          </button>
        </div>
      ) : (
        <span
          className={`text-xs ${sub.status === 'ACTIVE' ? 'text-slate-300' : 'text-slate-500 line-through italic'}`}
        >
          {sub.name}
        </span>
      )}
      {!editingName && (
        <div className="flex gap-2">
          <button
            onClick={() => setEditingName(true)}
            className="text-xs text-slate-600 hover:text-slate-400 underline"
          >
            Rename
          </button>
          <button
            onClick={() =>
              patch.mutate({ status: sub.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' })
            }
            className="text-xs text-slate-600 hover:text-slate-400 underline"
          >
            {sub.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      )}
    </li>
  );
}

// ─── Tables Tab ───────────────────────────────────────────────────────────────

function TablesTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  const {
    data: tables = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin-tables'],
    queryFn: () => api.get<AdminTable[]>('/api/admin/tables'),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin-tables'] });

  const filtered = tables.filter(
    (t) =>
      search === '' ||
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.entity_name.toLowerCase().includes(search.toLowerCase()) ||
      t.category_name.toLowerCase().includes(search.toLowerCase()),
  );

  if (isLoading) return <p className="text-slate-500 text-sm">Loading…</p>;
  if (error) return <p className="text-sm text-red-400">Error: {(error as Error).message}</p>;

  return (
    <div>
      <div className="mb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or entity…"
          className="w-72 rounded border border-aurora-strong bg-slate-800 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-500"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm italic text-slate-600">No tables match.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-aurora text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="pb-2 pr-4">Name</th>
              <th className="pb-2 pr-4">Entity / Category</th>
              <th className="pb-2 pr-4">Status</th>
              <th className="pb-2 pr-4">Team</th>
              <th className="pb-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-aurora">
            {filtered.map((t) => (
              <tr key={t.id} className="group">
                <td className="py-2 pr-4">
                  <span className="text-slate-200">{t.name}</span>
                  {t.archived_at && (
                    <span className="ml-2 rounded bg-yellow-900/40 px-1.5 py-0.5 text-xs text-yellow-400">
                      archived
                    </span>
                  )}
                </td>
                <td className="py-2 pr-4 text-xs text-slate-400">
                  {t.entity_name}
                  <span className="mx-1 text-slate-600">/</span>
                  {t.category_name}
                  {t.subcategory_name && (
                    <>
                      <span className="mx-1 text-slate-600">/</span>
                      {t.subcategory_name}
                    </>
                  )}
                </td>
                <td className="py-2 pr-4">
                  <span
                    className={`text-xs font-medium ${
                      t.status_flag === 'RED'
                        ? 'text-red-400'
                        : t.status_flag === 'YELLOW'
                          ? 'text-yellow-400'
                          : 'text-slate-500'
                    }`}
                  >
                    {t.status_flag}
                  </span>
                </td>
                <td className="py-2 pr-4 text-xs text-slate-500">{t.team_size}</td>
                <td className="py-2">
                  <button
                    onClick={() => setEditingId(t.id)}
                    className="text-xs text-slate-400 hover:text-slate-200 underline"
                  >
                    Manage
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editingId && (
        <EditTableModal
          table={tables.find((t) => t.id === editingId)!}
          isLeadership={user?.role === 'LEADERSHIP'}
          onClose={() => setEditingId(null)}
          onChanged={() => {
            invalidate();
            setEditingId(null);
          }}
        />
      )}
    </div>
  );
}

function EditTableModal({
  table,
  isLeadership,
  onClose,
  onChanged,
}: {
  table: AdminTable;
  isLeadership: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(table.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const rename = useMutation({
    mutationFn: () => api.patch(`/api/admin/tables/${table.id}`, { name }),
    onSuccess: onChanged,
    onError: (err: Error) => setError(err.message),
  });

  const unarchive = useMutation({
    mutationFn: () => api.post(`/api/tables/${table.id}/unarchive`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-archived'] });
      onChanged();
    },
    onError: (err: Error) => setError(err.message),
  });

  const archive = useMutation({
    mutationFn: () => api.post(`/api/tables/${table.id}/archive`, {}),
    onSuccess: onChanged,
    onError: (err: Error) => setError(err.message),
  });

  const del = useMutation({
    mutationFn: () => api.delete(`/api/tables/${table.id}`, { confirm_name: confirmName }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-deleted'] });
      onChanged();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-md rounded-lg border border-aurora-strong bg-slate-900 p-6 shadow-xl space-y-4">
        <h3 className="text-base font-medium text-slate-100">Manage Table</h3>

        <div className="space-y-1">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Location</p>
          <p className="text-sm text-slate-300">
            {table.entity_name}
            <span className="mx-1 text-slate-600">/</span>
            {table.category_name}
            {table.subcategory_name && (
              <>
                <span className="mx-1 text-slate-600">/</span>
                {table.subcategory_name}
              </>
            )}
          </p>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-slate-500 uppercase tracking-wider">Name</label>
          <div className="flex gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="flex-1 rounded border border-aurora-strong bg-slate-800 px-2 py-1.5 text-sm text-slate-200"
            />
            <button
              onClick={() => rename.mutate()}
              disabled={!name.trim() || name === table.name || rename.isPending}
              className="rounded bg-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
            >
              {rename.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <div className="flex gap-2 pt-1 border-t border-aurora">
          {table.archived_at ? (
            <button
              onClick={() => unarchive.mutate()}
              disabled={unarchive.isPending}
              className="rounded border border-aurora-strong px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-700 disabled:opacity-40"
            >
              {unarchive.isPending ? 'Restoring…' : 'Unarchive'}
            </button>
          ) : (
            <button
              onClick={() => archive.mutate()}
              disabled={archive.isPending}
              className="rounded border border-aurora-strong px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-700 disabled:opacity-40"
            >
              {archive.isPending ? 'Archiving…' : 'Archive'}
            </button>
          )}
        </div>

        {isLeadership && !confirmDelete && (
          <div className="border-t border-aurora pt-3">
            <button
              onClick={() => setConfirmDelete(true)}
              className="text-xs text-red-400 hover:text-red-300 underline"
            >
              Permanently delete…
            </button>
          </div>
        )}

        {isLeadership && confirmDelete && (
          <div className="border-t border-aurora pt-3 space-y-2">
            <p className="text-xs text-red-400">
              Type <span className="font-mono font-semibold">{table.name}</span> to confirm
              deletion.
            </p>
            <input
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder="Table name…"
              className="w-full rounded border border-red-800 bg-slate-800 px-2 py-1.5 text-sm text-slate-200"
            />
            <div className="flex gap-2">
              <button
                onClick={() => del.mutate()}
                disabled={confirmName !== table.name || del.isPending}
                className="rounded bg-red-700 px-3 py-1.5 text-xs text-white hover:bg-red-600 disabled:opacity-40"
              >
                {del.isPending ? 'Deleting…' : 'Delete permanently'}
              </button>
              <button
                onClick={() => {
                  setConfirmDelete(false);
                  setConfirmName('');
                }}
                className="text-xs text-slate-500 hover:text-slate-300"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex justify-end border-t border-aurora pt-3">
          <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-300">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Archived Tab ─────────────────────────────────────────────────────────────

function ArchivedTab() {
  const qc = useQueryClient();

  const {
    data: tables = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin-archived'],
    queryFn: () => api.get<ArchivedTable[]>('/api/admin/archived'),
  });

  const unarchive = useMutation({
    mutationFn: (id: string) => api.post(`/api/tables/${id}/unarchive`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-archived'] }),
  });

  if (isLoading) return <p className="text-slate-500 text-sm">Loading…</p>;
  if (error) return <p className="text-sm text-red-400">Error: {(error as Error).message}</p>;
  if (tables.length === 0) {
    return <p className="text-sm italic text-slate-600">No archived work efforts.</p>;
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-aurora text-left text-xs uppercase tracking-wider text-slate-500">
          <th className="pb-2 pr-4">Name</th>
          <th className="pb-2 pr-4">Entity / Category</th>
          <th className="pb-2 pr-4">Archived</th>
          <th className="pb-2 pr-4">By</th>
          <th className="pb-2" />
        </tr>
      </thead>
      <tbody className="divide-y divide-aurora">
        {tables.map((t) => (
          <tr key={t.id}>
            <td className="py-2 pr-4 text-slate-200">{t.name}</td>
            <td className="py-2 pr-4 text-slate-400">
              {t.entity_name} / {t.category_name}
            </td>
            <td className="py-2 pr-4 text-xs text-slate-500">
              {new Date(t.archived_at).toLocaleDateString()}
            </td>
            <td className="py-2 pr-4 text-xs text-slate-500">
              {t.archived_by?.display_name ?? '—'}
            </td>
            <td className="py-2">
              <button
                onClick={() => unarchive.mutate(t.id)}
                disabled={unarchive.isPending}
                className="text-xs text-slate-400 hover:text-slate-200 underline"
              >
                Unarchive
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Deleted Tab ──────────────────────────────────────────────────────────────

function DeletedTab() {
  const qc = useQueryClient();

  const {
    data: tables = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin-deleted'],
    queryFn: () => api.get<DeletedTable[]>('/api/admin/deleted'),
  });

  const restore = useMutation({
    mutationFn: (id: string) => api.post(`/api/tables/${id}/restore`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-deleted'] }),
  });

  if (isLoading) return <p className="text-slate-500 text-sm">Loading…</p>;
  if (error) return <p className="text-sm text-red-400">Error: {(error as Error).message}</p>;
  if (tables.length === 0) {
    return <p className="text-sm italic text-slate-600">No recently deleted work efforts.</p>;
  }

  function daysUntil(iso: string) {
    const diff = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / 86400_000));
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-aurora text-left text-xs uppercase tracking-wider text-slate-500">
          <th className="pb-2 pr-4">Name</th>
          <th className="pb-2 pr-4">Entity / Category</th>
          <th className="pb-2 pr-4">Deleted</th>
          <th className="pb-2 pr-4">By</th>
          <th className="pb-2 pr-4">Purge in</th>
          <th className="pb-2" />
        </tr>
      </thead>
      <tbody className="divide-y divide-aurora">
        {tables.map((t) => {
          const days = daysUntil(t.purge_at);
          return (
            <tr key={t.id}>
              <td className="py-2 pr-4 text-slate-400 line-through">{t.name}</td>
              <td className="py-2 pr-4 text-slate-500">
                {t.entity_name} / {t.category_name}
              </td>
              <td className="py-2 pr-4 text-xs text-slate-500">
                {new Date(t.deleted_at).toLocaleDateString()}
              </td>
              <td className="py-2 pr-4 text-xs text-slate-500">
                {t.deleted_by?.display_name ?? '—'}
              </td>
              <td
                className={`py-2 pr-4 text-xs font-medium ${days <= 3 ? 'text-red-400' : 'text-slate-500'}`}
              >
                {days}d
              </td>
              <td className="py-2">
                <button
                  onClick={() => restore.mutate(t.id)}
                  disabled={restore.isPending}
                  className="text-xs text-slate-400 hover:text-slate-200 underline"
                >
                  Restore
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── Audit Log Tab ────────────────────────────────────────────────────────────

function AuditTab() {
  const { user } = useAuth();
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
  if (entityType) params.set('entity_type', entityType);
  if (action) params.set('action', action);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-audit', entityType, action, offset],
    queryFn: () => api.get<AuditLogResponse>(`/api/admin/audit?${params.toString()}`),
  });

  const entries: AuditEntry[] = data?.entries ?? [];
  const total = data?.total ?? 0;

  return (
    <div>
      <div className="mb-4 flex gap-3">
        <select
          value={entityType}
          onChange={(e) => {
            setEntityType(e.target.value);
            setOffset(0);
          }}
          className="rounded border border-aurora-strong bg-slate-800 px-2 py-1 text-sm text-slate-200"
        >
          <option value="">All types</option>
          {['TABLE', 'NOTE', 'USER', 'ENTITY', 'CATEGORY', 'SUBCATEGORY'].map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={action}
          onChange={(e) => {
            setAction(e.target.value);
            setOffset(0);
          }}
          className="rounded border border-aurora-strong bg-slate-800 px-2 py-1 text-sm text-slate-200"
        >
          <option value="">All actions</option>
          {[
            'CREATE',
            'UPDATE',
            'DELETE',
            'FLAG_RAISE',
            'FLAG_LOWER',
            'MARK_REVIEWED',
            'TABLE_ARCHIVE',
            'SCOPE_UPDATE',
          ].map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        {isLoading && <span className="text-xs text-slate-500 self-center">Loading…</span>}
      </div>

      {entries.length === 0 && !isLoading ? (
        <p className="text-sm italic text-slate-600">No audit entries match the current filters.</p>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-aurora text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="pb-2 pr-4">Time</th>
                <th className="pb-2 pr-4">Actor</th>
                <th className="pb-2 pr-4">Type</th>
                <th className="pb-2 pr-4">Action</th>
                {user?.role === 'LEADERSHIP' && <th className="pb-2">Details</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-aurora">
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="py-2 pr-4 text-xs text-slate-500">
                    {new Date(e.occurred_at).toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 text-slate-300">{e.actor?.display_name ?? '—'}</td>
                  <td className="py-2 pr-4 text-slate-400">{e.entity_type}</td>
                  <td className="py-2 pr-4">
                    <span className="rounded bg-slate-700 px-1.5 py-0.5 text-xs text-slate-300">
                      {e.action}
                    </span>
                  </td>
                  {user?.role === 'LEADERSHIP' && (
                    <td className="py-2 max-w-xs truncate text-xs text-slate-500 font-mono">
                      {e.after ? JSON.stringify(e.after) : '—'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
            <span>
              {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                disabled={offset === 0}
                className="rounded border border-aurora px-2 py-1 hover:bg-slate-800 disabled:opacity-40"
              >
                ← Prev
              </button>
              <button
                onClick={() => setOffset(offset + LIMIT)}
                disabled={offset + LIMIT >= total}
                className="rounded border border-aurora px-2 py-1 hover:bg-slate-800 disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Support Tickets Tab ──────────────────────────────────────────────────────

function SupportTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['support-tickets'],
    queryFn: () => api.get<SupportTicket[]>('/api/support-tickets'),
  });

  if (isLoading) {
    return <p className="text-sm text-slate-500">Loading tickets…</p>;
  }
  if (error) {
    return (
      <p className="text-sm text-red-400">
        Could not load tickets: {(error as Error).message}
      </p>
    );
  }

  return <SupportTicketBoard initialTickets={data ?? []} />;
}
